import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, map, of, shareReplay, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ErrorHandlingService } from '../general/error-handling.service';
import { IPersonalPracticeSim } from 'src/app/interfaces/personalized-practice/ipersonalized-practice-simulation';
import { IPersonalizedPracticeSession } from 'src/app/interfaces/personalized-practice/ipersonalized-practice-session';
import { ICustomizationTypesResponse } from 'src/app/interfaces/personalized-practice/icustomization-option';
import { IActiveSimulation } from 'src/app/interfaces/personalized-practice/iactive-simulation';
import { IPersonalizedPracticeCallSummaryRequest } from 'src/app/interfaces/personalized-practice/ipersonalized-practice-call-summary-request';
import { IPersonalizedPracticeCallSummaryResponse } from 'src/app/interfaces/personalized-practice/ipersonalized-practice-call-summary-response';

@Injectable({
  providedIn: 'root',
})
export class PersonalizedPracticeService {
  backendUrl: string = environment.backendUrl;

  readonly hasPersonalizedPracticeProducts$: Observable<boolean> =
    this.getPersonalizedPracticeSimulations().pipe(
      map((modules: IPersonalPracticeSim[]) => modules.length > 0),
      catchError(() => {
        return of(false);
      }),
      shareReplay(1)
    );

  constructor(
    private http: HttpClient,
    private errorHandlingService: ErrorHandlingService
  ) {}

  getPersonalizedPracticeSimulations(): Observable<IPersonalPracticeSim[]> {
    return this.http
      .get<any>(`${this.backendUrl}/module/getPersonalizedPracticeModules/`)
      .pipe(
        map((result: IPersonalPracticeSim[]) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`getCustomerWithAccountsForCurrentUser: ${outcome}`);
        }),
        catchError(
          this.errorHandlingService.handleError<any>(
            `getCustomerWithAccountsForCurrentUser`
          )
        )
      );
  }

  getCustomizationTypes(
    moduleUuid: string,
    callUuid: string,
    accountUuid: string
  ): Observable<ICustomizationTypesResponse> {
    return this.http
      .get<ICustomizationTypesResponse>(
        `${this.backendUrl}/personalizedPractice/getCustomizationOptions?moduleUuid=${moduleUuid}&callUuid=${callUuid}&accountUuid=${accountUuid}`
      )
      .pipe(
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`getCustomizationTypes: ${outcome}`, {
            moduleUuid,
            callUuid,
            accountUuid,
          });
        }),
        catchError(
          this.errorHandlingService.handleError<ICustomizationTypesResponse>(
            `getCustomizationTypes`
          )
        )
      );
  }

  getActiveSimulations(moduleUuid: string): Observable<IActiveSimulation[]> {
    return this.http
      .get<any>(
        `${this.backendUrl}/call/findActiveCallsByModuleUuid?moduleUuid=${moduleUuid}`
      )
      .pipe(
        map((result: IActiveSimulation[]) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`getActiveSimulations: ${outcome}`);
        }),
        catchError(
          this.errorHandlingService.handleError<any>(`getActiveSimulations`)
        )
      );
  }

  getPersonalizedPracticeSessions(
    moduleUuid: string
  ): Observable<IPersonalizedPracticeSession[]> {
    return this.http
      .get<any>(
        `${this.backendUrl}/practiceCall/getPracticeCallStatsByModuleUuid?moduleUuid=${moduleUuid}`
      )
      .pipe(
        map((result: IActiveSimulation[]) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`getActiveSimulations: ${outcome}`);
        }),
        catchError(
          this.errorHandlingService.handleError<any>(`getActiveSimulations`)
        )
      );
  }

  getPersonalizedPracticeCallSummaryData(
    request: IPersonalizedPracticeCallSummaryRequest
  ): Observable<IPersonalizedPracticeCallSummaryResponse> {
    return this.http
      .post<IPersonalizedPracticeCallSummaryResponse>(
        `${this.backendUrl}/report/personalizedPractice/getPersonalizedPracticeCallSummaryData`,
        request
      )
      .pipe(
        map((result: IPersonalizedPracticeCallSummaryResponse) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`getPersonalizedPracticeCallSummaryData: ${outcome}`);
        }),
        catchError(
          this.errorHandlingService.handleError<IPersonalizedPracticeCallSummaryResponse>(
            `getPersonalizedPracticeCallSummaryData`
          )
        )
      );
  }
}
